"use client";

import { useSimBalance } from "@/hooks/useSimTokenInfo";
import { applyNativeGasBuffer } from "@/lib/tokens/gasBuffer";
import { isNativeTokenAddress } from "@/lib/tokens/nativeToken";
import { useCallback, useMemo } from "react";
interface TokenBalancePresetsResult {
  tokenBalanceWei: string | null;
  balanceDisplay: string;
  applyPreset: (percent: number) => void;
  isInsufficientBalance: boolean;
  isNativeToken: boolean;
}

/**
 * Shared hook for wallet balance fetching, preset calculations, and insufficient balance detection.
 * Used by both TokenAmountInput and TokenChainAmountInput.
 */
export function useTokenBalancePresets({
  workflowWalletAddress,
  tokenAddress,
  chainId,
  decimals,
  tokenPrice,
  tokenSymbol,
  amount,
  onAmountChange,
  onIsUserTyping
}: {
  workflowWalletAddress?: string;
  tokenAddress?: string;
  chainId?: number;
  decimals: number;
  tokenPrice?: number | null;
  tokenSymbol?: string;
  /** Current amount in wei — used for insufficient balance check */
  amount?: string;
  onAmountChange: (amount: string) => void;
  /** Called with false when a preset is applied (user is no longer typing) */
  onIsUserTyping?: (typing: boolean) => void;
}): TokenBalancePresetsResult {
  const balanceChainIds = useMemo(() => chainId ? [chainId] : undefined, [chainId]);
  const {
    data: walletBalanceData
  } = useSimBalance(workflowWalletAddress, balanceChainIds, !!(workflowWalletAddress && tokenAddress && chainId));
  const tokenBalanceWei = useMemo(() => {
    if (!walletBalanceData?.balances || !tokenAddress) return null;
    const match = walletBalanceData.balances.find(b => isNativeTokenAddress(tokenAddress) ? isNativeTokenAddress(b.address) : b.address.toLowerCase() === tokenAddress.toLowerCase());
    return match?.amount ?? null;
  }, [walletBalanceData, tokenAddress]);
  const balanceDisplay = useMemo(() => {
    if (!tokenBalanceWei) return `0 ${tokenSymbol ?? ""}`.trim();
    try {
      const bal = BigInt(tokenBalanceWei);
      const divisor = BigInt(10) ** BigInt(decimals);
      const whole = bal / divisor;
      const remainder = bal % divisor;
      const humanBalance = remainder === 0n ? whole.toString() : `${whole}.${remainder.toString().padStart(decimals, "0").replace(/0+$/, "")}`;
      const formatted = parseFloat(humanBalance).toLocaleString(undefined, {
        maximumFractionDigits: 6
      });
      const usd = tokenPrice ? parseFloat(humanBalance) * tokenPrice : null;
      const usdStr = usd != null ? ` ($${usd.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })})` : "";
      return `${formatted} ${tokenSymbol ?? ""}${usdStr}`.trim();
    } catch {
      return tokenSymbol ?? "";
    }
  }, [tokenBalanceWei, decimals, tokenPrice, tokenSymbol]);
  const applyPreset = useCallback((percent: number) => {
    if (!tokenBalanceWei) return;
    const balance = BigInt(tokenBalanceWei);
    let presetAmount = balance * BigInt(percent) / 100n;
    if (percent === 100) {
      presetAmount = applyNativeGasBuffer(presetAmount, tokenAddress, chainId ?? 0);
    }
    onAmountChange(presetAmount.toString());
    onIsUserTyping?.(false);
  }, [tokenBalanceWei, tokenAddress, chainId, onAmountChange, onIsUserTyping]);
  const isInsufficientBalance = useMemo(() => {
    if (!amount || !tokenBalanceWei) return false;
    try {
      return BigInt(amount) > BigInt(tokenBalanceWei);
    } catch {
      return false;
    }
  }, [amount, tokenBalanceWei]);
  const isNative = !!(tokenAddress && isNativeTokenAddress(tokenAddress));
  return {
    tokenBalanceWei,
    balanceDisplay,
    applyPreset,
    isInsufficientBalance,
    isNativeToken: isNative
  };
}